/**
 * Sudoku Board Component
 *
 * 9×9 grid with 3×3 box borders, cell selection highlighting,
 * pencil notes display, and number pad.
 *
 * Highlights: selected cell, same row/column/box, same number, errors.
 */

import React, { useCallback } from 'react';
import {
  SudokuBoard as SudokuBoardType,
  SudokuPosition,
  SudokuBoardProps,
  BOARD_SIZE,
  BOX_SIZE,
} from '../types';
import './SudokuBoard.css';

const SudokuBoard: React.FC<SudokuBoardProps> = ({
  board,
  selectedCell,
  onCellClick,
  disabled = false,
}) => {
  const isSameRow = useCallback(
    (row: number) => selectedCell !== null && selectedCell.row === row,
    [selectedCell]
  );

  const isSameCol = useCallback(
    (col: number) => selectedCell !== null && selectedCell.col === col,
    [selectedCell]
  );

  const isSameBox = useCallback(
    (row: number, col: number) => {
      if (!selectedCell) return false;
      const selBoxRow = Math.floor(selectedCell.row / BOX_SIZE);
      const selBoxCol = Math.floor(selectedCell.col / BOX_SIZE);
      const cellBoxRow = Math.floor(row / BOX_SIZE);
      const cellBoxCol = Math.floor(col / BOX_SIZE);
      return selBoxRow === cellBoxRow && selBoxCol === cellBoxCol;
    },
    [selectedCell]
  );

  const isSameNumber = useCallback(
    (row: number, col: number) => {
      if (!selectedCell) return false;
      const selectedValue = board[selectedCell.row]?.[selectedCell.col]?.value;
      if (selectedValue === null || selectedValue === undefined) return false;
      return board[row][col].value === selectedValue;
    },
    [selectedCell, board]
  );

  const isSelected = useCallback(
    (row: number, col: number) =>
      selectedCell !== null && selectedCell.row === row && selectedCell.col === col,
    [selectedCell]
  );

  const getCellClassName = useCallback(
    (row: number, col: number) => {
      const cell = board[row][col];
      const classes = ['sudoku-board__cell'];

      if (isSelected(row, col)) {
        classes.push('sudoku-board__cell--selected');
      } else if (isSameNumber(row, col)) {
        classes.push('sudoku-board__cell--same-number');
      } else if (isSameRow(row) || isSameCol(col) || isSameBox(row, col)) {
        classes.push('sudoku-board__cell--highlighted');
      }

      if (cell.isGiven) {
        classes.push('sudoku-board__cell--given');
      }

      if (cell.isError && cell.value !== null) {
        classes.push('sudoku-board__cell--error');
      }

      // Box border classes
      if (col % BOX_SIZE === 0 && col !== 0) {
        classes.push('sudoku-board__cell--box-left');
      }
      if (row % BOX_SIZE === 0 && row !== 0) {
        classes.push('sudoku-board__cell--box-top');
      }

      return classes.join(' ');
    },
    [board, isSelected, isSameNumber, isSameRow, isSameCol, isSameBox]
  );

  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (disabled) return;
      onCellClick({ row, col });
    },
    [disabled, onCellClick]
  );

  const renderNotes = (notes: number[]) => {
    if (notes.length === 0) return null;

    return (
      <div className="sudoku-board__notes" aria-label={`Notes: ${notes.join(', ')}`}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <span
            key={n}
            className={`sudoku-board__note ${notes.includes(n) ? 'sudoku-board__note--visible' : ''}`}
          >
            {notes.includes(n) ? n : ''}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="sudoku-board" role="grid" aria-label="Sudoku board">
      <div className="sudoku-board__grid">
        {board.map((row, rowIdx) => (
          <div key={rowIdx} className="sudoku-board__row" role="row">
            {row.map((cell, colIdx) => (
              <button
                key={`${rowIdx}-${colIdx}`}
                className={getCellClassName(rowIdx, colIdx)}
                onClick={() => handleCellClick(rowIdx, colIdx)}
                disabled={disabled}
                role="gridcell"
                aria-label={`Row ${rowIdx + 1}, Column ${colIdx + 1}${cell.value ? `, value ${cell.value}` : ', empty'}${cell.isGiven ? ', given' : ''}${cell.isError ? ', has conflict' : ''}`}
                aria-selected={isSelected(rowIdx, colIdx)}
                tabIndex={isSelected(rowIdx, colIdx) ? 0 : -1}
              >
                {cell.value !== null ? (
                  <span className="sudoku-board__cell-value">{cell.value}</span>
                ) : (
                  renderNotes(cell.notes)
                )}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export { SudokuBoard };
export default SudokuBoard;
